// Persisted PC keyboard shortcuts for the always-visible HUD action buttons --
// Menu, Equip, Interact, Mark, Cast, and each of the 4 quadrant hotbar slots.
// Movement (WASD/arrows) already works regardless of any of this; these are
// purely an alternative to clicking/tapping the buttons themselves. Stored as
// KeyboardEvent.code (layout/case-independent, e.g. "KeyE" regardless of
// whether Caps Lock is on) rather than .key, the same convention a real
// keybinding UI uses.
export type ActionId =
  | 'menu'
  | 'equip'
  | 'interact'
  | 'mark'
  | 'cast'
  | 'wait'
  | 'undo'
  | 'runes'
  | 'inventory'
  | 'quadrant1'
  | 'quadrant2'
  | 'quadrant3'
  | 'quadrant4';

export const ACTION_IDS: ActionId[] = [
  'menu',
  'equip',
  'interact',
  'mark',
  'cast',
  'wait',
  'undo',
  'runes',
  'inventory',
  'quadrant1',
  'quadrant2',
  'quadrant3',
  'quadrant4',
];

export const ACTION_LABELS: Record<ActionId, string> = {
  menu: 'Menu',
  equip: 'Equip',
  interact: 'Interact',
  mark: 'Mark',
  cast: 'Cast',
  wait: 'Wait (turn-based)',
  undo: 'Undo (turn-based)',
  runes: 'Runes',
  inventory: 'Inventory',
  quadrant1: 'Quadrant 1',
  quadrant2: 'Quadrant 2',
  quadrant3: 'Quadrant 3',
  quadrant4: 'Quadrant 4',
};

const STORAGE_KEY = 'rune-world-game-keybindings';
const HIDE_MAPPED_BUTTONS_KEY = 'rune-world-game-hide-mapped-buttons';

// Out-of-the-box mapping a fresh player sees before touching Settings. Still
// fully overridable/clearable per action via the Set/Clear buttons -- this
// only seeds what an untouched localStorage entry resolves to.
const DEFAULT_BINDINGS: Partial<Record<ActionId, string>> = {
  menu: 'KeyU',
  equip: 'KeyE',
  // Moved off KeyI (2026-07-27) to make room for the new Inventory action below --
  // Enter doubles as a natural "confirm" key for dialogue-box buttons anyway.
  interact: 'Enter',
  mark: 'KeyM',
  cast: 'Space',
  // Turn-mode only, and bound out of the box even though the buttons they
  // duplicate are hidden on the wall clock -- an unbound-by-default action is
  // one nobody discovers, and neither key is wanted for anything else. Period
  // for Wait is the roguelike convention for "pass a turn"; Z for undo is the
  // one every puzzle game already trained its players on.
  wait: 'Period',
  undo: 'KeyZ',
  runes: 'KeyR',
  inventory: 'KeyI',
  quadrant1: 'Digit1',
  quadrant2: 'Digit2',
  quadrant3: 'Digit3',
  quadrant4: 'Digit4',
};

function loadBindings(): Partial<Record<ActionId, string>> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return { ...DEFAULT_BINDINGS };
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : { ...DEFAULT_BINDINGS };
  } catch {
    return { ...DEFAULT_BINDINGS };
  }
}

let bindings: Partial<Record<ActionId, string>> = loadBindings();
let hideMappedButtons = localStorage.getItem(HIDE_MAPPED_BUTTONS_KEY) === 'true';
const listeners: Array<() => void> = [];

function persist(): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(bindings));
}

export function getKeybinding(action: ActionId): string | undefined {
  return bindings[action];
}

export function findActionForCode(code: string): ActionId | undefined {
  return ACTION_IDS.find((action) => bindings[action] === code);
}

// Assigning a code to an action steals it from whatever other action already
// held it -- two actions sharing one key would make the second one
// silently unreachable, which is worse than an explicit single owner per key.
export function setKeybinding(action: ActionId, code: string): void {
  for (const other of ACTION_IDS) {
    if (other !== action && bindings[other] === code) delete bindings[other];
  }
  bindings[action] = code;
  persist();
  listeners.forEach((listener) => listener());
}

export function clearKeybinding(action: ActionId): void {
  delete bindings[action];
  persist();
  listeners.forEach((listener) => listener());
}

export function getHideMappedButtons(): boolean {
  return hideMappedButtons;
}

export function setHideMappedButtons(value: boolean): void {
  hideMappedButtons = value;
  localStorage.setItem(HIDE_MAPPED_BUTTONS_KEY, String(value));
  listeners.forEach((listener) => listener());
}

// ui.ts refreshes the keybindings settings panel and HUD button
// visibility/labels whenever a binding or the hide-mapped-buttons preference
// changes, rather than waiting for the next unrelated render.
export function onKeybindingsChange(listener: () => void): void {
  listeners.push(listener);
}

// A short, human-readable label for a KeyboardEvent.code -- strips the
// Key/Digit prefixes real letters/numbers carry (e.g. "KeyE" -> "E",
// "Digit1" -> "1"); everything else (Space, Enter, ArrowUp, ...) is already
// a reasonable label as-is.
export function formatKeyCode(code: string): string {
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  return code;
}
